<template>
  <div class="tw-countdown-element">
    <div class="mx-auto max-w-[560px]">
      <template v-if="config?.type === 'textBased'">
        <div class="tw-countdown-element__text-based flex items-center justify-center">
          <p class="tw-box text-center">
            <span v-if="labelText" :style="labelTextStyle">{{ labelText }}&nbsp;</span>
            <template v-for="(value, key, index) in timeObject" :key="key">
              <span :style="countdownNumberStyle">{{ value }}&nbsp;</span>
              <span
                :style="countdownTextStyle"
                v-text="translate(`generate.countdownEl.period.${key}`, locale, { count: parseInt(value as string) })"
              />
              <span v-if="!isLastIndex(timeObject, index)">,&nbsp;</span>
            </template>
          </p>
        </div>
      </template>
      <template v-else>
        <div
          class="flex flex-col gap-16 text-center md:gap-24"
          :class="{
            'tw-countdown-element__boxed': config?.type === 'boxed',
            'tw-countdown-element__grid' : config?.type === 'grid',
          }"
        >
          <p v-if="labelText" :style="labelTextStyle">
            {{ labelText }}
          </p>
          <div class="flex justify-center gap-8 md:gap-12">
            <template v-for="(value, key) in timeObject" :key="key">
              <div class="tw-box">
                <span :style="countdownNumberStyle">{{ value }}</span>
                <span
                  :style="countdownTextStyle"
                  class="capitalize"
                  v-text="translate(`generate.countdownEl.period.shorten${key}`, locale, { count: parseInt(value as string) })"
                />
              </div>
            </template>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { type PropType, toRefs, computed, watch, ref, onBeforeMount, onBeforeUnmount } from 'vue';
import type { Element as ElementType, PageOptions } from '@shared/types/model';
import { useSpacing } from '@shared/composable/useSpacing';
import { getGeneralTextOptions } from '@shared/utils/getGeneralTextOptions';
import type { ElementOptions } from '@shared/types/options';
import { hexToRgba, globalEmit } from '@shared/utils/helpers';
import { useTranslate } from '@shared/composable/useTranslate';
import { formatSecondsAsTimeObject } from '@shared/elements/countdown/utils';

const props = defineProps({
  locale: { type: String, default: '' },
  isEditorMode: { type: Boolean, default: true },
  isPreviewMode: { type: Boolean, default: false },
  config: { type: Object as PropType<ElementOptions['countdown']>, default: () => ({}) },
  pageOptions: { type: Object as PropType<PageOptions>, default: () => ({}) },
  element: { type: Object as PropType<ElementType<'countdown'>>, default: () => ({}) },
  pageData: { type: Object, default: () => ({}) }
});

const { element, pageOptions } = toRefs(props);
const { padding, margin } = useSpacing(element);

const values = computed(() => JSON.parse(props.element.values || '{}'));
const labelText = computed(() => values.value.label || '');
const localeFormat = ref(props.pageOptions?.countdownOptions?.format);
const localeStartTime = ref(getFirstLocaleStartTime());
const timerInterval = ref<ReturnType<typeof setInterval>>();
const generalTextOptions = computed(() => getGeneralTextOptions({ element, pageOptions, optionKeys: ['labelText', 'countdownNumber', 'countdownText'] }));
const labelTextStyle = computed(() => generalTextOptions.value.labelText);
const countdownTextStyle = computed(() => generalTextOptions.value.countdownText);
const countdownNumberStyle = computed(() => generalTextOptions.value.countdownNumber);
const { translate } = useTranslate();

const boxStyle = computed(() => {
  const options = props.config || {};
  return {
    backgroundColor: hexToRgba(options.background?.color || '', options.background?.opacity),
    borderRadius: `${options.border?.radius || 0}px`,
    borderWidth: `${options.border?.width || 0}px`,
    borderColor: options.border?.color
  };
});

function isLastIndex(obj:Record<string, unknown>, index: number) {
  const keys = Object.keys(obj);
  return index === keys.length - 1;
}

function getFirstLocaleStartTime() {
  const elapsedTime = props.pageData?.elapsedCountdownTime || 0;
  const startTime = props.pageOptions?.countdownOptions?.startTime || 0;
  if (props.isEditorMode || props.isPreviewMode) return startTime;
  if (elapsedTime > 0) return Math.max(0, startTime - elapsedTime);
  return startTime;
}

function startTimer() {
  if (props.isEditorMode) return;
  globalEmit('countdownStarted', Date.now());
  const totalTime = localeStartTime.value || 0;
  const elapsedTime = props.pageData?.elapsedCountdownTime || 0;
  if (elapsedTime > 0 && !props.isPreviewMode) localeStartTime.value = Math.max(0, totalTime - elapsedTime);
  if (localeStartTime.value <= 0) return clearTimer();
  timerInterval.value = setInterval(() => {
    const time = localeStartTime.value || 0;
    if (time > 0) {
      localeStartTime.value = time - 1;
    } else {
      clearTimer();
    }
  }, 1000);
}

function clearTimer() {
  clearInterval(timerInterval.value);
}

const timeObject = computed(() => {
  const time = localeStartTime.value || 0;
  const format = localeFormat.value || 'DHMS';
  const isPadded = props.config?.type !== 'textBased';
  return formatSecondsAsTimeObject(time, format, isPadded);
});

watch(() => props?.config?.format, () => {
  // Show editor users selected format before save
  if (props.isEditorMode) localeFormat.value = props?.config?.format;
})

watch(() => props?.config?.startTime, () => {
  // Show editor users selected startTime before save
  if (props.isEditorMode) localeStartTime.value = props?.config?.startTime;
})

watch([() => pageOptions.value.countdownOptions?.format, () => pageOptions.value.countdownOptions?.startTime], () => {
  // Show editor users selected startTime/format after global options changed
  if (props.isEditorMode) {
    localeFormat.value = pageOptions.value.countdownOptions?.format;
    localeStartTime.value = pageOptions.value.countdownOptions?.startTime;
  }
})

onBeforeMount(startTimer)
onBeforeUnmount(clearTimer)
</script>

<style lang="postcss" scoped>
.tw-countdown-element {
  --box-bgColor: v-bind(boxStyle.backgroundColor);
  --box-borderWidth: v-bind(boxStyle.borderWidth);
  --box-borderColor: v-bind(boxStyle.borderColor);
  --box-borderRadius: v-bind(boxStyle.borderRadius);

  word-break: break-word;

  &__grid {
    .tw-box {
      @apply flex flex-col items-center p-12 min-w-64 md:min-w-[68px];
      background-color: var(--box-bgColor);
      border-width: var(--box-borderWidth, '1px');
      border-radius: var(--box-borderRadius, '6px');
      border-color: var(--box-borderColor, '#C3C9D5');
    }
  }
  &__text-based {
    .tw-box {
      @apply p-24 md:p-32;
      background-color: var(--box-bgColor);
      border-width: var(--box-borderWidth, '1px');
      border-radius: var(--box-borderRadius, '6px');
      border-color: var(--box-borderColor, '#C3C9D5');
      span {
        @apply inline-block;
      }
    }
  }
  &__boxed {
    @apply p-16 md:p-32;
    background-color: var(--box-bgColor);
    border-width: var(--box-borderWidth, '1px');
    border-radius: var(--box-borderRadius, '6px');
    border-color: var(--box-borderColor, '#C3C9D5');
    .tw-box {
      @apply flex flex-col items-center p-12 min-w-64 md:min-w-[68px];
    }
  }
}

/* Padding and margin settings */
.tw-countdown-element {
  --countdown-desktop-padding-top: v-bind('padding?.desktop?.top');
  --countdown-desktop-padding-left: v-bind('padding?.desktop?.left');
  --countdown-desktop-padding-bottom: v-bind('padding?.desktop?.bottom');
  --countdown-desktop-padding-right: v-bind('padding?.desktop?.right');

  --countdown-tablet-padding-top: v-bind('padding?.tablet?.top');
  --countdown-tablet-padding-left: v-bind('padding?.tablet?.left');
  --countdown-tablet-padding-bottom: v-bind('padding?.tablet?.bottom');
  --countdown-tablet-padding-right: v-bind('padding?.tablet?.right');

  --countdown-mobile-padding-top: v-bind('padding?.mobile?.top');
  --countdown-mobile-padding-left: v-bind('padding?.mobile?.left');
  --countdown-mobile-padding-bottom: v-bind('padding?.mobile?.bottom');
  --countdown-mobile-padding-right: v-bind('padding?.mobile?.right');

  --countdown-desktop-margin-top: v-bind('margin?.desktop?.top');
  --countdown-desktop-margin-left: v-bind('margin?.desktop?.left');
  --countdown-desktop-margin-bottom: v-bind('margin?.desktop?.bottom');
  --countdown-desktop-margin-right: v-bind('margin?.desktop?.right');

  --countdown-tablet-margin-top: v-bind('margin?.tablet?.top');
  --countdown-tablet-margin-left: v-bind('margin?.tablet?.left');
  --countdown-tablet-margin-bottom: v-bind('margin?.tablet?.bottom');
  --countdown-tablet-margin-right: v-bind('margin?.tablet?.right');

  --countdown-mobile-margin-top: v-bind('margin?.mobile?.top');
  --countdown-mobile-margin-left: v-bind('margin?.mobile?.left');
  --countdown-mobile-margin-bottom: v-bind('margin?.mobile?.bottom');
  --countdown-mobile-margin-right: v-bind('margin?.mobile?.right');

  padding-top: var(--countdown-mobile-padding-top, 0px);
  padding-left: var(--countdown-mobile-padding-left, 0px);
  padding-bottom: var(--countdown-mobile-padding-bottom, 0px);
  padding-right: var(--countdown-mobile-padding-right, 0px);

  margin-top: var(--countdown-mobile-margin-top, 0px);
  margin-left: var(--countdown-mobile-margin-left, 0px);
  margin-bottom: var(--countdown-mobile-margin-bottom, 0px);
  margin-right: var(--countdown-mobile-margin-right, 0px);

  @media (min-width: 768px) {
    padding-top: var(--countdown-tablet-padding-top, 0px);
    padding-left: var(--countdown-tablet-padding-left, 0px);
    padding-bottom: var(--countdown-tablet-padding-bottom, 0px);
    padding-right: var(--countdown-tablet-padding-right, 0px);

    margin-top: var(--countdown-tablet-margin-top, 0px);
    margin-left: var(--countdown-tablet-margin-left, 0px);
    margin-bottom: var(--countdown-tablet-margin-bottom, 0px);
    margin-right: var(--countdown-tablet-margin-right, 0px);
  }

  @media (min-width: 1024px) {
    padding-top: var(--countdown-desktop-padding-top, 0px);
    padding-left: var(--countdown-desktop-padding-left, 0px);
    padding-bottom: var(--countdown-desktop-padding-bottom, 0px);
    padding-right: var(--countdown-desktop-padding-right, 0px);

    margin-top: var(--countdown-desktop-margin-top, 0px);
    margin-left: var(--countdown-desktop-margin-left, 0px);
    margin-bottom: var(--countdown-desktop-margin-bottom, 0px);
    margin-right: var(--countdown-desktop-margin-right, 0px);
  }
}

.desktop {
  .tw-countdown-element {
    padding-top: var(--countdown-desktop-padding-top, 0px);
    padding-left: var(--countdown-desktop-padding-left, 0px);
    padding-bottom: var(--countdown-desktop-padding-bottom, 0px);
    padding-right: var(--countdown-desktop-padding-right, 0px);

    margin-top: var(--countdown-desktop-margin-top, 0px);
    margin-left: var(--countdown-desktop-margin-left, 0px);
    margin-bottom: var(--countdown-desktop-margin-bottom, 0px);
    margin-right: var(--countdown-desktop-margin-right, 0px);
  }
}

.tablet {
  .tw-countdown-element {
    padding-top: var(--countdown-tablet-padding-top, 0px);
    padding-left: var(--countdown-tablet-padding-left, 0px);
    padding-bottom: var(--countdown-tablet-padding-bottom, 0px);
    padding-right: var(--countdown-tablet-padding-right, 0px);

    margin-top: var(--countdown-tablet-margin-top, 0px);
    margin-left: var(--countdown-tablet-margin-left, 0px);
    margin-bottom: var(--countdown-tablet-margin-bottom, 0px);
    margin-right: var(--countdown-tablet-margin-right, 0px);
  }
}

.mobile {
  .tw-countdown-element {
    padding-top: var(--countdown-mobile-padding-top, 0px);
    padding-left: var(--countdown-mobile-padding-left, 0px);
    padding-bottom: var(--countdown-mobile-padding-bottom, 0px);
    padding-right: var(--countdown-mobile-padding-right, 0px);

    margin-top: var(--countdown-mobile-margin-top, 0px);
    margin-left: var(--countdown-mobile-margin-left, 0px);
    margin-bottom: var(--countdown-mobile-margin-bottom, 0px);
    margin-right: var(--countdown-mobile-margin-right, 0px);
  }
}
</style>
